"use client";

import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import {
	useDropzone,
	type Accept,
	type FileRejection,
	ErrorCode,
} from "react-dropzone";
import { Upload } from "lucide-react";

import { withOccurrenceKeys } from "../../lib/occurrence-keys";
import { cn } from "../../lib/utils";
import { FieldError } from "./field";
import { FileCard, formatBytes, type ExistingFile } from "./file-card";

const listFormatter = new Intl.ListFormat("en");

// A File has no id, and the same name can be picked twice, so each File object gets a key of its own.
const fileKeys = new WeakMap<File, string>();
let nextFileKey = 0;
function fileKey(file: File): string {
	let key = fileKeys.get(file);
	if (key === undefined) {
		key = `file-${nextFileKey++}`;
		fileKeys.set(file, key);
	}
	return key;
}

export function makeDescription(
	maxFiles: number,
	maxSizeInBytes: number,
	maxTotalSizeInBytes?: number,
	accept?: Accept,
): string {
	const parts: string[] = [];

	if (maxFiles > 1 && Number.isFinite(maxFiles)) {
		parts.push(`You can upload up to ${maxFiles} files.`);
	}

	const label = maxFiles === 1 ? "File" : "Files";
	parts.push(`${label} can be up to ${formatBytes(maxSizeInBytes)} each.`);

	if (maxTotalSizeInBytes && Number.isFinite(maxTotalSizeInBytes)) {
		parts.push(
			`Total upload size cannot exceed ${formatBytes(maxTotalSizeInBytes)}.`,
		);
	}

	if (accept) {
		const extensions = Array.from(new Set(Object.values(accept).flat()));
		if (extensions.length > 0) {
			parts.push(
				`You can upload ${listFormatter.format(extensions)} file formats.`,
			);
		}
	}

	return parts.join(" ");
}

type FileUploadBaseProps = {
	/** Applied to the underlying file input, so an external `<label htmlFor>` can target it. */
	id?: string;
	name?: string;
	/** Defaults to `<id>-file-upload-input` when an `id` is given. */
	"data-testid"?: string;
	accept?: Accept;
	maxFiles?: number;
	/** Per-file size limit passed to react-dropzone. Defaults to 100 MB. */
	maxSizeInBytes?: number;
	/** Cumulative size limit across all files (new + existing). No limit if omitted. */
	maxTotalSizeInBytes?: number;
	disabled?: boolean;
	isInvalid?: boolean;
	existingFiles?: ExistingFile[];
	onRemoveExisting?: (file: ExistingFile) => void;
	onBlur?: () => void;
	hideFileSize?: boolean;
	className?: string;
};

export type FileUploadSingleProps = FileUploadBaseProps & {
	multiple?: false;
	value?: File | null;
	onChange?: (file: File | null) => void;
};

export type FileUploadMultipleProps = FileUploadBaseProps & {
	multiple: true;
	value?: File[];
	onChange?: (files: File[]) => void;
};

export type FileUploadProps = FileUploadSingleProps | FileUploadMultipleProps;

const FileUpload = React.forwardRef<HTMLDivElement, FileUploadProps>(
	(
		{
			id,
			name,
			"data-testid": dataTestId,
			value,
			onChange,
			onBlur,
			accept,
			maxFiles: maxFilesProp,
			maxSizeInBytes = 100 * 1024 * 1024,
			maxTotalSizeInBytes,
			multiple = false,
			disabled = false,
			isInvalid = false,
			existingFiles = [],
			onRemoveExisting,
			hideFileSize = false,
			className,
		},
		ref,
	) => {
		const maxFiles = maxFilesProp ?? (multiple ? Infinity : 1);

		const files = useMemo(() => {
			if (multiple) {
				return (value as File[] | undefined) ?? [];
			}
			const singleValue = value as File | null | undefined;
			return singleValue ? [singleValue] : [];
		}, [value, multiple]);

		const [fileRejections, setFileRejections] = useState<FileRejection[]>([]);

		const totalSlots = files.length + existingFiles.length;
		const remainingSlots = Math.max(0, maxFiles - totalSlots);
		const showDropzone = remainingSlots > 0;

		const handleDrop = useCallback(
			(accepted: File[], rejected: FileRejection[]) => {
				setFileRejections(rejected);
				if (!onChange) {
					return;
				}

				if (multiple) {
					(onChange as (files: File[]) => void)([...files, ...accepted]);
				} else {
					(onChange as (file: File | null) => void)(accepted[0] ?? null);
				}
			},
			[onChange, files, multiple],
		);

		const handleRemove = useCallback(
			(file: File | ExistingFile) => {
				if ("id" in file) {
					onRemoveExisting?.(file as ExistingFile);
					return;
				}

				if (!onChange) {
					return;
				}

				if (multiple) {
					(onChange as (files: File[]) => void)(
						files.filter((f) => f !== file),
					);
				} else {
					(onChange as (file: File | null) => void)(null);
				}

				setFileRejections((prev) => prev.filter((r) => r.file !== file));
			},
			[onChange, files, multiple, onRemoveExisting],
		);

		const { getRootProps, getInputProps, isDragActive } = useDropzone({
			onDrop: handleDrop,
			accept,
			maxSize: maxSizeInBytes,
			maxFiles: remainingSlots,
			disabled,
			multiple,
			onFileDialogCancel: onBlur,
		});

		const cumulativeFileSize = useMemo(() => {
			const newSize = files.reduce((acc, f) => acc + f.size, 0);
			const existingSize = existingFiles.reduce((acc, f) => acc + f.size, 0);
			return newSize + existingSize;
		}, [files, existingFiles]);

		const hasTooManyFilesRejection = fileRejections.some((r) =>
			r.errors.some((e) => e.code === ErrorCode.TooManyFiles),
		);

		const allFiles: (File | ExistingFile)[] = useMemo(
			() => [...existingFiles, ...files],
			[existingFiles, files],
		);

		return (
			<div
				ref={ref}
				data-slot="file-upload"
				className={cn("space-y-2", className)}
			>
				{showDropzone && (
					<div
						{...getRootProps()}
						className={cn(
							"relative flex min-h-[120px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors duration-200 bg-background",
							disabled && "cursor-not-allowed opacity-50",
							isDragActive && "border-blue-400 bg-blue-50",
							isInvalid && !isDragActive && "border-danger-300 bg-danger-50",
							!isDragActive &&
								!isInvalid &&
								"border-neutral-200 hover:border-neutral-300",
						)}
					>
						<input
							{...getInputProps({ id, name })}
							data-testid={
								dataTestId ?? (id ? `${id}-file-upload-input` : undefined)
							}
						/>

						<Upload className="mb-2 h-8 w-8 text-neutral-400" />

						<p className="text-center text-sm text-neutral-600">
							Drag and drop or{" "}
							<span className="font-medium text-blue-500">
								select file{multiple ? "s" : ""}
							</span>
						</p>

						<p className="mt-2 max-w-md text-center text-xs text-neutral-500">
							{makeDescription(
								maxFiles,
								maxSizeInBytes,
								maxTotalSizeInBytes,
								accept,
							)}
						</p>
					</div>
				)}

				{hasTooManyFilesRejection ? (
					<FieldError>
						{maxFiles === 1
							? "You can only upload 1 file."
							: Number.isFinite(maxFiles)
								? `You can upload up to ${maxFiles} files.`
								: "Too many files were selected."}
					</FieldError>
				) : (
					fileRejections.map((rejection) => (
						<FileCard
							key={fileKey(rejection.file)}
							file={rejection.file}
							disabled={disabled}
							onRemove={handleRemove}
							isInvalid
							errors={rejection.errors}
							maxSizeInBytes={maxSizeInBytes}
							hideFileSize={hideFileSize}
						/>
					))
				)}

				{withOccurrenceKeys(allFiles, (file) =>
					"id" in file ? `existing-${file.id}` : fileKey(file),
				).map(({ item: file, key }) => (
					<FileCard
						key={key}
						file={file}
						disabled={disabled}
						onRemove={handleRemove}
						hideFileSize={hideFileSize}
						maxSizeInBytes={maxSizeInBytes}
					/>
				))}

				{maxTotalSizeInBytes && cumulativeFileSize > maxTotalSizeInBytes && (
					<FieldError>
						The total size of your files is too large. The combined limit is{" "}
						{formatBytes(maxTotalSizeInBytes)}.
					</FieldError>
				)}
			</div>
		);
	},
);
FileUpload.displayName = "FileUpload";

export { FileUpload };
